import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Zap,
  Upload,
  BarChart2,
  Users,
  Check,
  ArrowRight,
  Star,
  Clock,
  Shield,
  ChevronDown,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { LanguageSwitch } from '../../i18n/LanguageSwitch'
import { useLocale } from '../../i18n/LocaleProvider'

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
}

const stagger = {
  animate: { transition: { staggerChildren: 0.1 } },
}

// Mock product preview component
function ProductPreview() {
  const { t } = useLocale()
  const candidates = [
    {
      name: 'Sarah Mitchell',
      score: 94,
      skills: ['Python', 'AWS', 'ML'],
      exp: t('landing.preview.years', { count: 7 }),
      status: 'shortlisted',
      why: t('landing.preview.whyExample'),
    },
    {
      name: 'James Okonkwo',
      score: 87,
      skills: ['React', 'Node.js', 'SQL'],
      exp: t('landing.preview.years', { count: 5 }),
      status: 'shortlisted',
      why: null,
    },
    {
      name: 'Priya Sharma',
      score: 79,
      skills: ['Java', 'Spring', 'Docker'],
      exp: t('landing.preview.years', { count: 6 }),
      status: 'pending',
      why: null,
    },
    {
      name: 'Thomas Bergmann',
      score: 61,
      skills: ['PHP', 'MySQL', 'CSS'],
      exp: t('landing.preview.years', { count: 3 }),
      status: 'pending',
      why: null,
    },
  ]

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-50'
    if (score >= 70) return 'text-blue-600 bg-blue-50'
    if (score >= 50) return 'text-yellow-600 bg-yellow-50'
    return 'text-red-600 bg-red-50'
  }

  const getBarColor = (score) => {
    if (score >= 90) return 'bg-green-500'
    if (score >= 70) return 'bg-blue-500'
    if (score >= 50) return 'bg-yellow-500'
    return 'bg-red-400'
  }

  return (
    <div className="relative">
      <div className="absolute inset-0 bg-brand-500 opacity-10 blur-3xl rounded-3xl" />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">

        {/* Recommended shortlist banner */}
        <div className="bg-green-600 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-white shrink-0" />
            <span className="text-sm font-bold text-white">
              {t('landing.preview.topCandidates')}
            </span>
          </div>
          <span className="text-xs text-green-200 font-medium">
            {t('landing.preview.jobTitle')}
          </span>
        </div>

        {/* App header */}
        <div className="bg-gray-50 border-b border-gray-100 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-brand-600 flex items-center justify-center">
              <Zap className="w-3 h-3 text-white" />
            </div>
            <span className="text-sm font-semibold text-gray-700">
              {t('landing.preview.rankedCount')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
              {t('landing.preview.topMatch')}
            </span>
          </div>
        </div>

        {/* Score bands */}
        <div className="grid grid-cols-4 gap-2 px-4 py-3 border-b border-gray-50">
          {[
            { label: t('landing.preview.scoreBands.excellent'), count: 1, color: 'bg-green-50 text-green-700' },
            { label: t('landing.preview.scoreBands.good'), count: 2, color: 'bg-blue-50 text-blue-700' },
            { label: t('landing.preview.scoreBands.partial'), count: 1, color: 'bg-yellow-50 text-yellow-700' },
            { label: t('landing.preview.scoreBands.poor'), count: 0, color: 'bg-red-50 text-red-700' },
          ].map((band) => (
            <div key={band.label} className={`rounded-xl p-2 text-center ${band.color}`}>
              <p className="text-lg font-bold">{band.count}</p>
              <p className="text-xs font-medium">{band.label}</p>
            </div>
          ))}
        </div>

        {/* Candidate list */}
        <div className="divide-y divide-gray-50">
          {candidates.map((c, i) => (
            <div
              key={i}
              className={`px-4 py-3 ${
                c.status === 'shortlisted' ? 'bg-green-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-gray-400 w-4">
                  #{i + 1}
                </span>
                <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-brand-700">
                    {c.name.charAt(0)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-900">
                      {c.name}
                    </span>
                    {c.status === 'shortlisted' && (
                      <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1">
                        <Star className="w-2.5 h-2.5 fill-green-500" />
                        {t('landing.preview.reviewBadge')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full ${getBarColor(c.score)}`}
                        style={{ width: `${c.score}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 shrink-0">
                      {c.exp}
                    </span>
                  </div>
                  {c.why && (
                    <p className="text-xs text-green-700 mt-1 font-medium">
                      ✓ {c.why}
                    </p>
                  )}
                </div>
                <span
                  className={`text-sm font-bold px-2 py-1 rounded-lg shrink-0 ${getScoreColor(c.score)}`}
                >
                  {c.score}%
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="bg-gray-50 border-t border-gray-100 px-4 py-2.5 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {t('landing.preview.bottomNote')}
          </span>
          <button className="text-xs font-semibold text-brand-600 flex items-center gap-1">
            {t('landing.preview.export')} <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  )
}

export function LandingPage() {
  const navigate = useNavigate()
  const { t } = useLocale()
  const [openFaq, setOpenFaq] = useState(null)

  const plans = ['free', 'starter', 'pro', 'business']

  const localizedFaqs = Array.from({ length: 6 }, (_, i) => ({
    q: t(`landing.faq.${i}.q`),
    a: t(`landing.faq.${i}.a`),
  }))

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-100 backdrop-blur-sm bg-opacity-95">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-brand-600 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-gray-900 text-lg">WorkflowPro</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitch />
            <button
              onClick={() => navigate('/signin')}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              {t('publicNav.signIn')}
            </button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/upload')}
              icon={<Zap className="w-3.5 h-3.5" />}
            >
              {t('landing.navCta')}
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-20 pb-16 px-4 bg-gradient-to-b from-brand-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left - copy */}
            <div>
              <motion.div {...fadeUp}>
                <span className="inline-flex items-center gap-2 bg-brand-100 text-brand-700 text-sm font-semibold px-4 py-2 rounded-full mb-6">
                  <Zap className="w-3.5 h-3.5" />
                  {t('landing.eyebrow')}
                </span>
              </motion.div>

              <motion.h1
                className="text-5xl sm:text-6xl font-bold text-gray-900 leading-tight mb-5"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
              >
                <span className="block">{t('landing.headline1')}</span>
                <span className="block text-brand-600">{t('landing.headline2')}</span>
                <span className="block">{t('landing.headline3')}</span>
              </motion.h1>

              <motion.p
                className="text-xl text-gray-500 mb-8 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                {t('landing.subheading')}
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row gap-3 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <Button
                  variant="primary"
                  size="xl"
                  onClick={() => navigate('/upload')}
                  icon={<Upload className="w-5 h-5" />}
                  className="shadow-lg"
                >
                  {t('landing.primaryCta')}
                </Button>
                <Button
                  variant="outline"
                  size="xl"
                  onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {t('landing.pricingCta')}
                </Button>
              </motion.div>

              <motion.div
                className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 pt-6 border-t border-gray-100"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                {[
                  { icon: Shield, text: t('landing.trustPrivacy') },
                  { icon: Clock, text: t('landing.trustRanking') },
                  { icon: CheckCircle, text: t('landing.trustFree') },
                  { icon: Zap, text: t('landing.trustSetup') },
                ].map((item) => {
                  const Icon = item.icon
                  return (
                    <span key={item.text} className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                      <Icon className="w-4 h-4 text-brand-600 shrink-0" />
                      {item.text}
                    </span>
                  )
                })}
              </motion.div>
            </div>

            {/* Right - product preview */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="hidden lg:block lg:scale-[1.25] lg:origin-center"
            >
              <ProductPreview />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social proof bar */}
      <section className="py-6 px-4 bg-gray-50 border-y border-gray-100">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12">
          {[
            { value: t('landing.proofReview'), label: t('landing.proofReviewLabel') },
            { value: t('landing.proofReduce'), label: t('landing.proofReduceLabel') },
            { value: t('landing.proofFree'), label: t('landing.proofFreeLabel') },
            { value: t('landing.proofEu'), label: t('landing.proofEuLabel') },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-lg font-bold text-brand-600">{stat.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              {t('landing.howTitle')}
            </h2>
            <p className="text-gray-500 text-lg max-w-3xl mx-auto leading-relaxed">
              {t('landing.howIntro')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Upload,
                title: t('landing.step1Title'),
                description: t('landing.step1Description'),
                color: 'bg-blue-50 text-blue-600',
              },
              {
                icon: Zap,
                title: t('landing.step2Title'),
                description: t('landing.step2Description'),
                color: 'bg-brand-50 text-brand-600',
              },
              {
                icon: CheckCircle,
                title: t('landing.step3Title'),
                description: t('landing.step3Description'),
                color: 'bg-green-50 text-green-600',
              },
            ].map((item, i) => {
              const Icon = item.icon
              return (
                <div key={i} className="card p-8 text-center h-full">
                  <div className={`w-14 h-14 rounded-2xl ${item.color} flex items-center justify-center mx-auto mb-4`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              {t('landing.benefitsTitle')}
            </h2>
            <p className="text-gray-500 text-lg max-w-3xl mx-auto leading-relaxed">
              {t('landing.benefitsIntro')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: Clock,
                title: t('landing.benefit1Title'),
                description: t('landing.benefit1Description'),
                color: 'bg-blue-50 text-blue-600',
              },
              {
                icon: Star,
                title: t('landing.benefit2Title'),
                description: t('landing.benefit2Description'),
                color: 'bg-brand-50 text-brand-600',
              },
              {
                icon: Shield,
                title: t('landing.benefit3Title'),
                description: t('landing.benefit3Description'),
                color: 'bg-green-50 text-green-600',
              },
              {
                icon: Zap,
                title: t('landing.benefit4Title'),
                description: t('landing.benefit4Description'),
                color: 'bg-purple-50 text-purple-600',
              },
            ].map((benefit) => {
              const Icon = benefit.icon
              return (
                <div key={benefit.title} className="flex gap-4 rounded-2xl bg-white border border-gray-100 p-6">
                  <div className={`w-12 h-12 rounded-2xl ${benefit.color} flex items-center justify-center shrink-0`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 bg-white" id="pricing">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              {t('landing.pricingTitle')}
            </h2>
            <p className="text-gray-500 text-lg">{t('landing.pricingIntro')}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                key: 'free',
                name: t('landing.plans.free.name'),
                price: t('landing.plans.free.price'),
                audience: t('landing.plans.free.audience'),
                features: [
                  t('landing.plans.free.features.0'),
                  t('landing.plans.free.features.1'),
                  t('landing.plans.free.features.2'),
                  t('landing.plans.free.features.3'),
                ],
                cta: t('landing.plans.free.cta'),
              },
              {
                key: 'starter',
                name: t('landing.plans.starter.name'),
                price: t('landing.plans.starter.price'),
                audience: t('landing.plans.starter.audience'),
                features: [
                  t('landing.plans.starter.features.0'),
                  t('landing.plans.starter.features.1'),
                  t('landing.plans.starter.features.2'),
                  t('landing.plans.starter.features.3'),
                  t('landing.plans.starter.features.4'),
                ],
                cta: t('landing.plans.starter.cta'),
              },
              {
                key: 'pro',
                name: t('landing.plans.pro.name'),
                price: t('landing.plans.pro.price'),
                audience: t('landing.plans.pro.audience'),
                features: [
                  t('landing.plans.pro.features.0'),
                  t('landing.plans.pro.features.1'),
                  t('landing.plans.pro.features.2'),
                  t('landing.plans.pro.features.3'),
                ],
                cta: t('landing.plans.pro.cta'),
              },
              {
                key: 'business',
                name: t('landing.plans.business.name'),
                price: t('landing.plans.business.price'),
                audience: t('landing.plans.business.audience'),
                features: [
                  t('landing.plans.business.features.0'),
                  t('landing.plans.business.features.1'),
                  t('landing.plans.business.features.2'),
                ],
                cta: t('landing.plans.business.cta'),
              },
            ].map((plan) => {
              const isPopular = plan.key === 'pro'
              return (
                <div
                  key={plan.key}
                  className={`relative rounded-2xl border-2 p-5 ${isPopular ? 'border-brand-600 shadow-lg' : 'border-gray-200'}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="bg-brand-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                        {t('landing.popular')}
                      </span>
                    </div>
                  )}
                  <div className="mb-4">
                    <p className="font-bold text-gray-900 text-lg">{plan.name}</p>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="text-3xl font-bold text-gray-900">{plan.price}</span>
                    </div>
                    <p className="text-gray-500 text-sm mt-2 leading-relaxed">{plan.audience}</p>
                  </div>
                  <ul className="space-y-2 mb-5">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-gray-600">
                        <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={isPopular ? 'primary' : 'secondary'}
                    fullWidth
                    onClick={() => navigate(plan.key === 'free' ? '/upload' : '/signup')}
                  >
                    {plan.cta}
                  </Button>
                </div>
              )
            })}
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('landing.faqTitle')}</h2>
          </div>
          <div className="space-y-3">
            {localizedFaqs.map((faq, i) => (
              <div key={i} className="card overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-b from-brand-600 to-brand-700">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {t('landing.finalTitle')}
          </h2>
          <p className="text-brand-200 text-lg mb-8">
            {t('landing.finalDescription')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              size="xl"
              onClick={() => navigate('/upload')}
              className="bg-white text-brand-700 hover:bg-brand-50 shadow-lg"
            >
              {t('landing.finalStart')}
            </Button>
            <Button
              variant="ghost"
              size="xl"
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-white hover:bg-white/10"
            >
              {t('landing.pricingCta')}
            </Button>
          </div>
          <p className="text-brand-300 text-sm mt-4">
            {t('landing.noCard')}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-gray-900">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-brand-600 flex items-center justify-center">
              <Zap className="w-3 h-3 text-white" />
            </div>
            <span className="font-bold text-white text-sm">WorkflowPro</span>
          </div>
          <p className="text-gray-500 text-sm">{t('landing.copyright')}</p>
          <div className="flex gap-4 text-sm text-gray-500">
            <a href="mailto:hello@getworkflowpro.com" className="hover:text-white transition-colors">{t('landing.contact')}</a>
            <a href="#pricing" className="hover:text-white transition-colors">{t('landing.pricing')}</a>
            <Link to="/privacy" className="hover:text-white transition-colors">{t('landing.privacy')}</Link>
            <Link to="/terms" className="hover:text-white transition-colors">{t('landing.terms')}</Link>
            <Link to="/cookies" className="hover:text-white transition-colors">{t('landing.cookies')}</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
